import { useEffect, useCallback } from 'react';

const STORAGE_KEY = 'marketing_attribution';
const EXPIRY_DAYS = 30;

export interface MarketingAttribution {
  marketer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  first_touch_url?: string;
  last_touch_url?: string;
  first_touch_at?: number;
  last_touch_at?: number;
  expires_at?: number;
}

interface StoredAttribution extends MarketingAttribution {
  expires_at: number;
}

function getStoredAttribution(): StoredAttribution | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const data: StoredAttribution = JSON.parse(stored);
    
    // Check if expired
    if (data.expires_at && data.expires_at < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    return data;
  } catch {
    return null;
  }
}

function setStoredAttribution(data: StoredAttribution): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage might be full or disabled
  }
}

export function getMarketingAttribution(): MarketingAttribution | null {
  return getStoredAttribution();
}

export function clearMarketingAttribution(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function useMarketingAttribution(): void {
  const captureAttribution = useCallback(() => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    
    // Check if there are any marketing params to capture
    const newMarketer = params.get('marketer');
    const newUtmSource = params.get('utm_source');
    const newUtmMedium = params.get('utm_medium');
    const newUtmCampaign = params.get('utm_campaign');
    const newUtmContent = params.get('utm_content');
    const newUtmTerm = params.get('utm_term');
    const newGclid = params.get('gclid');
    const newFbclid = params.get('fbclid');
    
    const hasNewParams = newMarketer || newUtmSource || newUtmMedium || 
                         newUtmCampaign || newUtmContent || newUtmTerm || 
                         newGclid || newFbclid;
    
    const existingData = getStoredAttribution();
    const now = Date.now();
    const expiresAt = now + (EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    
    if (!existingData) {
      // First touch - store everything
      const newData: StoredAttribution = {
        marketer: newMarketer || undefined,
        utm_source: newUtmSource || undefined,
        utm_medium: newUtmMedium || undefined,
        utm_campaign: newUtmCampaign || undefined,
        utm_content: newUtmContent || undefined,
        utm_term: newUtmTerm || undefined,
        gclid: newGclid || undefined,
        fbclid: newFbclid || undefined,
        referrer: document.referrer || undefined,
        first_touch_url: window.location.href,
        last_touch_url: window.location.href,
        first_touch_at: now,
        last_touch_at: now,
        expires_at: expiresAt,
      };
      setStoredAttribution(newData);
    } else if (hasNewParams) {
      // Subsequent visit with new params - update last touch
      const updatedData: StoredAttribution = {
        ...existingData,
        // Update with new values if present, keep old if not
        marketer: newMarketer || existingData.marketer,
        utm_source: newUtmSource || existingData.utm_source,
        utm_medium: newUtmMedium || existingData.utm_medium,
        utm_campaign: newUtmCampaign || existingData.utm_campaign,
        utm_content: newUtmContent || existingData.utm_content,
        utm_term: newUtmTerm || existingData.utm_term,
        gclid: newGclid || existingData.gclid,
        fbclid: newFbclid || existingData.fbclid,
        last_touch_url: window.location.href,
        last_touch_at: now,
        expires_at: expiresAt, // Reset expiry on new touch
      };
      setStoredAttribution(updatedData);
    }
  }, []);

  useEffect(() => {
    captureAttribution();
  }, [captureAttribution]);
}
